/**
 * User Interaction Provider
 * 
 * Context provider for managing credential and MFA modals.
 */

import React, { createContext, useState, useCallback, useEffect } from 'react';
import { CredentialModal } from './CredentialModal';
import { MFAModal } from './MFAModal';
import { ErrorDisplay } from '../common/ErrorDisplay';
import { ComponentErrorBoundary } from '../common/ErrorBoundary';
import { storeCredentials, respondToInteraction } from '../../services/userInteractionService';
import { 
  CredentialFields, 
  CredentialData, 
  InteractionRequest,
  UserInteractionContextType 
} from '../../types/userInteraction';

export const UserInteractionContext = createContext<UserInteractionContextType | null>(null);

// Environment-aware logging utility
const logger = {
  info: (msg: string, ...args: any[]) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(msg, ...args);
    }
  },
  error: (msg: string, error?: any) => {
    console.error(msg, error);
  }
};

interface UserInteractionProviderProps {
  children: React.ReactNode;
}

export const UserInteractionProvider: React.FC<UserInteractionProviderProps> = ({ children }) => {
  const [credentialModalOpen, setCredentialModalOpen] = useState(false);
  const [mfaModalOpen, setMFAModalOpen] = useState(false);
  
  const [currentPlatformId, setCurrentPlatformId] = useState<number | null>(null);
  const [currentPlatformName, setCurrentPlatformName] = useState<string>('');
  const [currentCredentialFields, setCurrentCredentialFields] = useState<CredentialFields>({});
  const [currentRequest, setCurrentRequest] = useState<InteractionRequest | null>(null);
  
  const [credentialResolve, setCredentialResolve] = useState<((value: CredentialData) => void) | null>(null);
  const [credentialReject, setCredentialReject] = useState<((reason?: any) => void) | null>(null);
  
  // Error state for UI display instead of alert()
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /**
   * Show credential modal and wait for user input
   */
  const showCredentialModal = useCallback((
    platformId: number,
    credentialFields: CredentialFields,
    platformName: string = 'this platform'
  ): Promise<CredentialData> => {
    return new Promise((resolve, reject) => {
      setCurrentPlatformId(platformId);
      setCurrentPlatformName(platformName);
      setCurrentCredentialFields(credentialFields);
      setCredentialResolve(() => resolve);
      setCredentialReject(() => reject);
      setCredentialModalOpen(true);
    });
  }, []);

  /**
   * Show MFA modal for interaction request
   */
  const showMFAModal = useCallback((request: InteractionRequest) => {
    setCurrentRequest(request);
    setMFAModalOpen(true);
  }, []);

  /**
   * Close all modals
   */
  const closeModal = useCallback(() => {
    setCredentialModalOpen(false);
    setMFAModalOpen(false);
    
    if (credentialReject) {
      credentialReject(new Error('User cancelled'));
    }
    
    setCredentialResolve(null);
    setCredentialReject(null);
    setCurrentRequest(null);
  }, [credentialReject]);

  /**
   * Handle credential submission
   */
  const handleCredentialSubmit = useCallback(async (credentials: CredentialData) => {
    if (!currentPlatformId) return;

    try {
      // Store credentials as USER BROWSER credentials (not organization credentials)
      const { UserBrowserCredentialService } = await import('../../services/userBrowserCredentialService');
      await UserBrowserCredentialService.storeCredentials(currentPlatformId, credentials);
      
      logger.info('[UserInteraction] User browser credentials stored successfully');
      
      // Resolve promise
      if (credentialResolve) {
        credentialResolve(credentials);
      }
      
      // Close modal
      setCredentialModalOpen(false);
      setCredentialResolve(null);
      setCredentialReject(null);
      setErrorMessage(null);
      
    } catch (error) {
      logger.error('[UserInteraction] Error storing credentials:', error);
      setErrorMessage(`Error storing credentials: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [currentPlatformId, credentialResolve]);

  /**
   * Handle MFA code submission
   */
  const handleMFASubmit = useCallback(async (code: string) => {
    if (!currentRequest) return;

    try {
      // Submit response via API
      await respondToInteraction(currentRequest.uuid, { 
        [currentRequest.request_type === 'mfa_token' ? 'mfa_code' : 'code']: code 
      });
      
      // Close modal
      setMFAModalOpen(false);
      setCurrentRequest(null);
      setErrorMessage(null);
      
    } catch (error) {
      logger.error('[UserInteraction] Error submitting MFA code:', error);
      setErrorMessage(`Error submitting code: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [currentRequest]);

  /**
   * Listen for WebSocket messages
   */
  useEffect(() => {
    const handleWebSocketMessage = (event: CustomEvent) => {
      const data = event.detail;
      
      if (data.type === 'interaction_required') {
        logger.info('[UserInteraction] Interaction required');
        
        // Map request_uuid to uuid for InteractionRequest type
        const request: InteractionRequest = {
          uuid: data.request_uuid,
          job_id: data.job_id,
          job_group_id: data.job_group_id,
          request_type: data.request_type,
          prompt_message: data.prompt_message,
          expires_at: data.expires_at,
          created_at: data.created_at || new Date().toISOString()
        };
        
        logger.info('[UserInteraction] Mapped request');
        showMFAModal(request);
      }
    };

    // Listen for custom events from WebSocket
    document.addEventListener('websocket-message', handleWebSocketMessage as EventListener);

    return () => {
      document.removeEventListener('websocket-message', handleWebSocketMessage as EventListener);
    };
  }, [showMFAModal]);

  const contextValue: UserInteractionContextType = {
    showCredentialModal,
    showMFAModal,
    closeModal,
    isModalOpen: credentialModalOpen || mfaModalOpen
  };

  return (
    <UserInteractionContext.Provider value={contextValue}>
      {children}
      
      {/* Error Display */}
      {errorMessage && (
        <div className="fixed top-4 right-4 z-[100000] max-w-md">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 shadow-lg">
            <div className="flex items-start">
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                  Error
                </h4>
                <p className="text-sm text-red-700 dark:text-red-300">
                  {errorMessage}
                </p>
              </div>
              <button
                onClick={() => setErrorMessage(null)}
                className="ml-3 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                aria-label="Dismiss error"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
      
      <ComponentErrorBoundary>
        <CredentialModal
          isOpen={credentialModalOpen}
          platformName={currentPlatformName}
          credentialFields={currentCredentialFields}
          onSubmit={handleCredentialSubmit}
          onCancel={closeModal}
        />
      </ComponentErrorBoundary>
      
      <ComponentErrorBoundary>
        <MFAModal
          isOpen={mfaModalOpen}
          request={currentRequest}
          onSubmit={handleMFASubmit}
          onCancel={closeModal}
        />
      </ComponentErrorBoundary>
    </UserInteractionContext.Provider>
  );
};
